import {
  Box,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  alpha,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { userLogout } from "../../../api/auth";
import { APP_COLORS, ENUM_USER_ROLE } from "../../../enums";
import { ROUTE_PATHS } from "../../../routes/paths";
import { convertToFullName } from "../../../services/utils";
import { useAppDispatch, useAppSelector } from "../../../store";
import { ButtonMotion } from "../../form/buttons";
import { PhosphorMuiIcon } from "../../icons";
import Logo from "../../Logo";
import NotificationPopover from "../../notification";
import {
  HeaderIconButton,
  HeaderIconsContainer,
  HeaderProfileAvatar,
  HeaderProfileChevron,
  HeaderProfileDivider,
  HeaderProfileEmail,
  HeaderProfileInfo,
  HeaderProfileName,
  HeaderProfileSection,
  HeaderProfileTextContainer,
} from "./header.styles";
import { CaretDownIcon, ListDashes, SwapIcon, UserIcon, SignOutIcon } from "@phosphor-icons/react";

export interface HeaderProps {
  breadcrumbs?: any;
  showMenuButton?: boolean;
  onMenuClick?: () => void;
}

export function Header({ breadcrumbs, showMenuButton, onMenuClick }: HeaderProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const dispatch = useAppDispatch();
  const userData = useAppSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const _handleProfileAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onTransferClick = () => {
    navigate(`${ROUTE_PATHS?.SETTINGS}?type=transfer_management_rights`);
  };

  const logout = async () => {
    dispatch(userLogout());
  };

  const onProfileClick = () => {
    const route =
      userData?.role === ENUM_USER_ROLE.VENDOR
        ? navigate({
          pathname: ROUTE_PATHS?.MY_PROFILE,
          search: "?type=profile",
        })
        : navigate(ROUTE_PATHS?.MY_PROFILE);
  };

  const profileData = useAppSelector((state) => state.profile?.data);
  const fullName = profileData
    ? convertToFullName(profileData)
    : userData?.firstName || userData?.email || "";
  const userEmail = userData?.email || "";
  const profilePicture = profileData?.picture;
  const profileInitial =
    profileData?.firstName?.[0] || userData?.firstName?.[0] || "";

  return (
    <Grid
      container
      justifyContent={"space-between"}
      alignItems={"center"}
      sx={{ height: { xs: 56, sm: 60, lg: 62 } }}
    >
      <Grid
        item
        xs={showMenuButton ? true : 12}
        md={6}
        sx={showMenuButton ? { minWidth: 0 } : undefined}
      >
        <Box display={"flex"} alignItems={"center"} gap={1} minWidth={0}>
          {showMenuButton && (
            <IconButton
              color="inherit"
              onClick={onMenuClick}
              aria-label="Open menu"
              edge="start"
              sx={{ mr: 0.5, flexShrink: 0 }}
            >
              <PhosphorMuiIcon icon={ListDashes} />
            </IconButton>
          )}
          <Logo white style={{ width: "auto", height: 36, maxHeight: 36 }} />
        </Box>
      </Grid>
      <Grid
        item
        xs={showMenuButton ? "auto" : 12}
        md={6}
        display={"flex"}
        justifyContent={"end"}
        sx={showMenuButton ? { flexShrink: 0 } : undefined}
      >
        <HeaderIconsContainer alignItems={"center"} sx={showMenuButton ? { flexWrap: "nowrap" } : undefined}>
          {(userData?.role === ENUM_USER_ROLE.VENDOR) && (profileData?.licenseForMeeting > 0) && (
            <ButtonMotion>
              <HeaderIconButton
                onClick={onTransferClick}
                title="Transfer Management Rights"
                aria-label="Transfer Management Rights"
              >
                <PhosphorMuiIcon icon={SwapIcon} />
              </HeaderIconButton>
            </ButtonMotion>
          )}
          <NotificationPopover
            notifications={userData?.notifications}
            iconButtonSx={{
              width: 38,
              height: 38,
              minWidth: 38,
              minHeight: 38,
              border: `1px solid ${APP_COLORS.headerDivider}`,
              borderRadius: "42px",
              padding: "0px 10.5556px",
              color: APP_COLORS.headerIconColor,
              "& svg": {
                width: 20,
                height: 20,
                color: APP_COLORS.headerIconColor,
                "& path": {
                  fill: APP_COLORS.headerIconColor,
                },
              },
              "&:hover": {
                backgroundColor: alpha(APP_COLORS.headerIconColor, 0.1),
                borderColor: APP_COLORS.headerIconColor,
              },
              "&.MuiIconButton-sizeSmall": {
                width: 38,
                height: 38,
                minWidth: 38,
                minHeight: 38,
              },
            }}
          />
          <HeaderProfileDivider />
          <HeaderProfileSection
            onClick={_handleProfileAvatarClick}
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <HeaderProfileAvatar src={profilePicture}>
              {profileInitial}
            </HeaderProfileAvatar>
            {!showMenuButton && (
              <HeaderProfileInfo>
                <HeaderProfileTextContainer>
                  <HeaderProfileName variant="standard">
                    {fullName}
                  </HeaderProfileName>
                  <HeaderProfileEmail variant="standard">
                    {userEmail}
                  </HeaderProfileEmail>
                </HeaderProfileTextContainer>
                <HeaderProfileChevron>
                  <PhosphorMuiIcon icon={CaretDownIcon} color="inherit" />
                </HeaderProfileChevron>
              </HeaderProfileInfo>
            )}
          </HeaderProfileSection>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              sx: { borderRadius: 4, marginTop: 1, minWidth: 220 }
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem onClick={onProfileClick}>
              <ListItemIcon>
                <PhosphorMuiIcon icon={UserIcon} color="action" fontSize="small" />
              </ListItemIcon>
              <Typography variant="body-ibmPlexMono">My Profile</Typography>
            </MenuItem>

            <Divider />

            <MenuItem onClick={logout}>
              <ListItemIcon>
                <PhosphorMuiIcon icon={SignOutIcon} color="error" fontSize="small" />
              </ListItemIcon>
              <Typography color="error" variant="body-ibmPlexMono">Log out</Typography>
            </MenuItem>
          </Menu>
        </HeaderIconsContainer>
      </Grid>
    </Grid>
  );
}
